import { HSMSPageHeaderWrapper, HSMSStepContentCard, HSMSTableAction, PageBreadcrumbs } from '@components/shared';
import { Button, PageHeader, Table } from 'antd';
import React, { ReactText, useState } from 'react';
import { useMutation, useSubscription } from '@apollo/client';
import {
  DELETE_RESULT_SHEET,
  delete_result_sheetMutation,
  delete_result_sheetMutationVariables,
  RESULT_SHEETS,
  result_sheetsSubscription,
  result_sheetsSubscriptionVariables,
} from '@app-types/graphql-types';
import { ResultSheetStatusTag, resultSheetTourSteps } from '@modules/resultSheet';
import Link from 'next/link';
import { useToasts } from 'react-toast-notifications';
import { useMst } from '@models/Root';
import { AppTours } from '@modules/app';

export const ManageResultSheets = () => {
  const [actionToggle, setActionToggle] = useState(false);
  const [selectedRecordIDs, setSelectedRecordIDs] = useState<string[]>([]);
  const { addToast } = useToasts();
  const { user } = useMst();
  const { data: resultSheetsData, loading: resultSheetsDataLoading } = useSubscription<
    result_sheetsSubscription,
    result_sheetsSubscriptionVariables
  >(RESULT_SHEETS, {
    fetchPolicy: 'no-cache',
    variables: {
      where: {
        ...(!user.userRoles.includes('academicHead') && {
          user_public: {
            id: {
              _eq: user.id,
            },
          },
        }),
      },
    },
  });

  const [deleteResultSheet, { loading: deleteResultSheetLoading }] = useMutation<
    delete_result_sheetMutation,
    delete_result_sheetMutationVariables
  >(DELETE_RESULT_SHEET, {
    onCompleted: async (data) => {
      let affected_rows = 0;
      if (data.delete_result_sheet) {
        affected_rows = data.delete_result_sheet.affected_rows;
      }

      addToast(`${affected_rows} result sheet${affected_rows > 1 ? 's' : ''} successfully deleted.`, {
        appearance: 'success',
        autoDismiss: true,
      });
    },
    onError: () => {
      addToast(`Error. Something went wrong. Please try again.`, {
        appearance: 'error',
        autoDismiss: true,
      });
    },
  });

  const columns = [
    {
      title: 'Course code',
      dataIndex: ['batch_class', 'batch', 'course', 'code'],
      key: 'courseCode',
    },
    {
      title: 'Prepared by',
      dataIndex: 'preparedBy',
      key: 'preparedBy',
      render: (_, record: result_sheetsSubscription['result_sheet'][0]) => {
        return (
          <span>
            {record.user_public.firstName} {record.user_public.lastName}
          </span>
        );
      },
    },
    {
      title: 'Subject',
      dataIndex: ['batch_subject_configuration', 'subject', 'name'],
      key: 'subjectName',
    },
    {
      title: 'Subject Code',
      dataIndex: ['batch_subject_configuration', 'subject', 'code'],
      key: 'subjectCode',
    },
    {
      title: 'Batch',
      dataIndex: ['batch_class', 'batch', 'name'],
      key: 'batch',
    },
    {
      title: 'Class',
      dataIndex: ['batch_class', 'name'],
      key: 'batchClass',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => {
        return <ResultSheetStatusTag status={text} />;
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right' as const,
      width: 100,
      render: (record: result_sheetsSubscription['result_sheet'][0]) => {
        const isOwnResultSheet = record.user_public.id === user.id;

        return (
          <span>
            <Link
              href={
                isOwnResultSheet && record.status !== 'approved'
                  ? `/result-sheets/${record.id}/update`
                  : `/result-sheets/${record.id}`
              }
            >
              <Button
                type={isOwnResultSheet && record.status !== 'approved' ? 'default' : 'primary'}
                ghost={!isOwnResultSheet}
                size="small"
              >
                {isOwnResultSheet && record.status !== 'approved' ? 'Edit' : 'View'}
              </Button>
            </Link>
          </span>
        );
      },
    },
  ];

  const rowSelection = {
    onChange: (selectedRowKeys: ReactText[] | number[], selectedRows: result_sheetsSubscription['result_sheet']) => {
      if (selectedRows.length > 0) {
        setActionToggle(true);
      } else {
        setActionToggle(false);
      }

      setSelectedRecordIDs(selectedRowKeys as string[]);
    },
    getCheckboxProps: (record: result_sheetsSubscription['result_sheet'][0]) => ({
      disabled: record.status === 'approved',
    }),
  };

  const ownedResultSheetData = resultSheetsData?.result_sheet.filter(
    (resultSheet) => resultSheet.user_public.id === user.id,
  );
  return (
    <div>
      <HSMSPageHeaderWrapper>
        <PageHeader title="Result Sheets" subTitle="Manage result sheets" />
      </HSMSPageHeaderWrapper>

      <PageBreadcrumbs
        paths={[
          {
            pathName: 'Result Sheets',
            url: '/result-sheets',
          },
        ]}
      />

      <AppTours steps={resultSheetTourSteps.manageResultSheets} />

      <HSMSStepContentCard>
        <div className="hsms-card-body">
          <Table
            loading={resultSheetsDataLoading || deleteResultSheetLoading}
            rowKey="id"
            dataSource={resultSheetsData?.result_sheet || []}
            pagination={false}
            bordered
            columns={columns}
            rowSelection={rowSelection}
            title={() => {
              return (
                <>
                  <HSMSTableAction
                    statistics={[
                      {
                        title: 'Total Result Sheets',
                        value: resultSheetsData?.result_sheet?.length || 0,
                      },
                      {
                        title: 'Pending Result Sheets',
                        value:
                          resultSheetsData?.result_sheet?.filter((resultSheet) => resultSheet.status === 'pending')
                            .length || 0,
                      },
                      {
                        title: 'Rejected Result Sheets',
                        value:
                          resultSheetsData?.result_sheet?.filter((resultSheet) => resultSheet.status === 'notApproved')
                            .length || 0,
                      },
                    ]}
                    actionTypes={[
                      {
                        id: 'delete-btn',
                        component: 'Button',
                        className: 'step-action-button-margin',
                        title: 'Delete',
                        show: actionToggle,
                        onClick: async () => {
                          await deleteResultSheet({
                            variables: {
                              where: {
                                id: {
                                  _in: selectedRecordIDs,
                                },
                              },
                            },
                          });
                        },
                      },
                    ]}
                  />
                </>
              );
            }}
          />
        </div>
      </HSMSStepContentCard>
    </div>
  );
};
